import { z } from 'zod';
import { TransformDetails } from 'libs/end-to-end/src/types/databases/transform/transform-details';
import { TaggedDataSetReference } from 'libs/end-to-end/src/types/databases/data-sets/data-set-reference';
import { UnixTimestamp } from 'libs/end-to-end/src/types/databases/analysis/database-analysis';
import { ScriptSourceUserUpload } from 'libs/end-to-end/src/types/databases/script-source-user-upload';
import { PlatformTransformEntrypoint } from 'libs/end-to-end/src/types/databases/transform/platform-transform-entrypoints';

export const TransformReferenceStatuses = [
  'Succeeded',
  'Failed',
  'Running',
  'NotStarted',
] as const;

export const TransformReferenceStatusesEnum = z.enum(
  TransformReferenceStatuses,
);

export const PlatformTransformDetails = z.object({
  transformEntrypoint: PlatformTransformEntrypoint,
});

export type PlatformTransformDetails = z.infer<typeof PlatformTransformDetails>;

export const TransformScriptSource = z.union([
  ScriptSourceUserUpload,
  z.object({
    type: z.literal('other-transform'),
    transformId: z.string(),
  }),
]);

export type TransformScriptSource = z.infer<typeof TransformScriptSource>;

export const Transform = z.object({
  id: z.string(),
  startedAt: UnixTimestamp,
  dataSetReferences: z.array(TaggedDataSetReference),
  details: TransformDetails,
  runOnBatch: z.boolean().optional(),
  runOnPlatformTransform: z.boolean().default(false),
  platformTransformDetails: PlatformTransformDetails.nullable().default(null),
  transformIdToCompare: z.string().nullable().default(null),
  runOnDagsterTransform: z.boolean().default(false),
  dagsterTransformId: z.string().nullable().default(null),
});

export type Transform = z.infer<typeof Transform>;

export const TransformWithOutputConsoleUrl = Transform.extend({
  outputConsoleUrl: z.string(),
});

export type TransformWithOutputConsoleUrl = z.infer<
  typeof TransformWithOutputConsoleUrl
>;

export const TransformReference = z.object({
  id: z.string(),
  status: TransformReferenceStatusesEnum,
  startedAt: UnixTimestamp,
  updatedAt: UnixTimestamp,
});

export type TransformReference = z.infer<typeof TransformReference>;

export const TransformReferences = z.object({
  transforms: z.array(TransformReference),
});

export type TransformReferences = z.infer<typeof TransformReferences>;

export const TransformReferenceWithOutputConsoleUrl = TransformReference.extend(
  {
    outputConsoleUrl: z.string(),
  },
);

export type TransformReferenceWithOutputConsoleUrl = z.infer<
  typeof TransformReferenceWithOutputConsoleUrl
>;

export const Transforms = z.object({
  transforms: z.array(Transform),
});

export type Transforms = z.infer<typeof Transforms>;

export const TransformReferencesWithOutputConsoleUrls = z
  .object({
    transforms: z.array(TransformReferenceWithOutputConsoleUrl),
  })
  .describe('TransformReferencesWithOutputConsoleUrls');

export type TransformReferencesWithOutputConsoleUrls = z.infer<
  typeof TransformReferencesWithOutputConsoleUrls
>;

export const TransformReferencesPaginatedWithOutputConsoleUrls = z
  .object({
    transforms: z.array(TransformReferenceWithOutputConsoleUrl),
    totalCount: z.number(),
    page: z.number(),
    pageSize: z.number(),
  })
  .describe('TransformReferencesPaginatedWithOutputConsoleUrls');

export type TransformReferencesPaginatedWithOutputConsoleUrls = z.infer<
  typeof TransformReferencesPaginatedWithOutputConsoleUrls
>;

export const TransformsWithOutputConsoleUrls = Transforms.extend({
  transforms: z.array(TransformWithOutputConsoleUrl),
}).describe('TransformsWithOutputConsoleUrls');

export type TransformsWithOutputConsoleUrls = z.infer<
  typeof TransformsWithOutputConsoleUrls
>;
